import React from 'react';
import floralBorder from '../images/flowerBorder.png';
import LilyImage from '../images/LilyImage.jpg';

const tributes = [
  {
    name: 'Lily Collins',
    birthYear: 1948,
    deathYear: 2024,
    blurb:
      "We would like to extend a heartfelt dedication to Lilly, who has coordinated Emma's Court since its inception. She played a vital role in maintaining this garden until her passing in 2024 and always maintained hope for the garden, even during challenging times. People naturally regarded her as a leader, and she possessed the ability to unite them.",
    image: LilyImage,
  },
  // Add more tributes here
];

/* ─── Featured (first) tribute ───────────────────────────────────────────────── */

function FeaturedTribute({ tribute }) {
  return (
    <article className="overflow-hidden border border-[#d5cfc0] bg-[#fffdf7]">
      {/* Floral header band */}
      <div
        className="h-36 w-full bg-no-repeat bg-top sm:h-44"
        style={{
          backgroundImage: `url(${floralBorder})`,
          backgroundSize: '100% auto',
        }}
        aria-hidden="true"
      />

      {/* Portrait — overlaps floral band */}
      <div className="relative -mt-20 flex justify-center">
        <img
          src={tribute.image}
          alt={tribute.name}
          className="h-40 w-40 rounded-full border-4 border-[#fffdf7] object-cover shadow-lg select-none pointer-events-none"
          draggable="false"
        />
      </div>

      {/* Text */}
      <div className="px-8 pb-10 pt-5 text-center sm:px-12">
        <h2 className="font-serif text-3xl text-[#10351f] sm:text-4xl">{tribute.name}</h2>
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#70806f]">
          {tribute.birthYear} – {tribute.deathYear}
        </p>
        <div className="mx-auto mt-6 h-px w-16 bg-[#cfc7b6]" />
        <p className="mt-6 text-base italic leading-8 text-[#445044] sm:text-lg sm:leading-9">
          "{tribute.blurb}"
        </p>
        <p className="mt-7 text-2xl select-none" aria-label="In remembrance" role="img">
          🕊
        </p>
      </div>
    </article>
  );
}

/* ─── Additional (grid) tribute card ─────────────────────────────────────────── */

function TributeCard({ tribute }) {
  return (
    <article className="overflow-hidden border border-[#d5cfc0] bg-[#fffdf7]">
      <div
        className="h-28 w-full bg-no-repeat bg-top"
        style={{
          backgroundImage: `url(${floralBorder})`,
          backgroundSize: '100% auto',
        }}
        aria-hidden="true"
      />
      <div className="relative -mt-14 flex justify-center">
        <img
          src={tribute.image}
          alt={tribute.name}
          className="h-28 w-28 rounded-full border-4 border-[#fffdf7] object-cover shadow-md select-none pointer-events-none"
          draggable="false"
        />
      </div>
      <div className="px-6 pb-8 pt-4 text-center">
        <h3 className="font-serif text-2xl text-[#10351f]">{tribute.name}</h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#70806f]">
          {tribute.birthYear} – {tribute.deathYear}
        </p>
        <div className="mx-auto mt-4 h-px w-10 bg-[#cfc7b6]" />
        <p className="mt-4 text-sm italic leading-7 text-[#445044]">"{tribute.blurb}"</p>
        <p className="mt-5 text-xl select-none" aria-label="In remembrance" role="img">🕊</p>
      </div>
    </article>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────────── */

function TributePage() {
  const [featured, ...rest] = tributes;

  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">

      {/* ── Hero + featured tribute — all above the fold ──────────── */}
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto grid max-w-[92rem] grid-cols-1 gap-10 px-6 py-12 sm:px-10 lg:grid-cols-[1fr_minmax(22rem,0.9fr)] lg:gap-16 lg:px-16 lg:py-16 xl:gap-20">

          {/* Left: page intro */}
          <div className="flex flex-col justify-center">
            <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl lg:text-7xl">
              In Memoriam
            </h1>
            <div className="mt-5 h-px w-24 bg-[#304536]" />
            <p className="font-handwritten mt-5 max-w-md text-xl leading-snug text-[#243c2c] sm:text-2xl">
              Remembering the people who shaped this garden.
            </p>
            <p className="mt-6 max-w-md text-base leading-7 text-[#445044]">
              Remembering the wonderful people who have contributed to our garden and community.
              Their care, dedication, and spirit continue to bloom in every season.
            </p>

          </div>

          {/* Right: featured tribute card */}
          {featured && <FeaturedTribute tribute={featured} />}
        </div>
      </section>

      {/* ── Additional tributes (shown only when there are more) ── */}
      {rest.length > 0 && (
        <section className="bg-[#f1eadc]">
          <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
            <div className="mb-8 border-b border-[#cfc7b6] pb-5">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Cherished members
              </p>
              <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">Memorial</h2>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((tribute, i) => (
                <TributeCard key={i} tribute={tribute} />
              ))}
            </div>
          </div>
        </section>
      )}

    </div>
  );
}

export default TributePage;
